import { ActionType, ActionTypes } from '../actions/index';
import { IFetchStateAction } from '../actions/settings-fetch';
import { PriceType } from '../classes/price-type';
import IAltText from '../classes/alt-text';
import { SeatingOptionGroup } from '../classes/seating-option';
import { Level } from '../classes/level';
import { Performance } from '../classes/performance';
import { ItemType } from '../classes/item-type';
import { IExchangeTicket } from '../classes/ticket-exchange';
import { IPromoCodeDetails } from '../classes/promo-code-details';
import { SeatZone } from '../classes/seat';

export interface IAppSettingsModel {
    requesting: false;
    altText: IAltText;
    googleAnalyticsId: string;
    brandId: string;
    itemName: string;
    itemNameRichText: string;
    performanceDate: Date;
    performanceTimeZone: string;
    itemOnSale: boolean;
    itemAvailable: boolean;
    promoCodeOnly: boolean;
    currencyFormat: string;
    currentCulture: string;
    language: string;
    currencyFormatCountries: Array<string>;
    performanceDateString: string;
    performanceTimeString: string;
    itemType: ItemType;
    isVariablePriceType: boolean;
    itemId: number;
    maxTicketQuantity: number;
    minTicketQuantity: number;
    groupTicketMessaging: string;
    reserveErrorMessage: string;
    cartConflictErrorHeading: string;
    cartTimerWithCountMessage: string;
    cartTimerMessage: string;
    cartCount: number;
    cartConflictErrorMessageText: string;
    mapScale: number;
    mapScaleSensitivity: number;
    mapMaxZoom: number;
    showZoomControlRight: boolean;
    seatFetchErrorMessage: string;
    cartUrl: string;
    myAccountUrl: string;
    priceTypeSettings: Array<PriceType>;
    currentPromoCode: IPromoCodeDetails;
    svgDefaultFillColor: string;
    svgHoverFillColor: string;
    svgStrokeColor: string;
    svgStrokeWidth: number;
    svgSelectedFillColor: string;
    renderAisleSeatToggle: string;
    renderAdditionalSeatingOptions: string;
    renderAdditionalSeatingOptionsBelowTicketQuantity: boolean;
    openAdditionalSeatingOptionsOnLoad: boolean;
    renderAdditionalNotes: boolean;
    showPerformanceListOnLoad: string;
    additionalSeatingOptions: Array<SeatingOptionGroup>;
    additionalNotesTitle: string;
    additionalNotesPlaceholder: string;

    priceTypeCriteriaNotReachedShowAsModal: boolean;
    forcePatronsReserveInMultiples: number;
    forcePatronsReserveWholeRow: boolean;
    forceMultipleCartMessageText: string;
    forceMultipleFilterMessageText: string;
    minimumTicketsPerSeatGroup: number;

    loadingText: string;
    loadingSvgText: string;

    cartSubtotalLabel: string;
    cartEmptyNotice: string;
    seatIsInCartMessage: string;
    groupSeatsInCartMessage: string;
    groupSeatCartItemText: string;
    seatsRemainingText: string;
    seatHoverMissingPriceTypesMessage: string;

    cartAddMoreButton: string;
    cartCheckoutButton: string;
    cartMessage: string;

    filterDateAndTimeTitle: string;
    filterPriceRangeTitle: string;
    filterPriceRangeMessage: string;
    filterPriceRangeMin: string;
    filterPriceRangeMax: string;

    addToCartButtonText: string;
    updateCartButtonText: string;
    viewCartButtonText: string;
    goToCartButtonText: string;

    clearButton: string;

    filterFoundToast: string;
    filterSearchingToast: string;
    filterToastThreshold: string;
    filterErrorToast: string;

    filterProceedButton: string;
    filterQuickCheckoutButton: string;

    filterAdditionalSeatingOptionsTitle: string;
    filterAdditionalSeatingOptionsMessage: string;

    gaZoneSelectTicketMessage: string;
    aisleAccessSeatsLabel: string;
    canBeSeparatedLabel: string;

    legendText: string;
    legendModalText: string;
    changeLevelsButtonText: string;

    missingSelectedSeatingOptionNotice: string;
    missingAttestationErrorMessageHeader: string;
    missingAttestationErrorMessageText: string;
    missingAttestationErrorMessageButton: string;
    attestationSeatCancelButton: string;
    attestationSeatConfirmationButton: string;

    leaveOrphanedSeatsMessage: string;
    venueSvgHighlightShading: string;
    seatmapSvgViewBox: string;
    seatmapBackgroundImage: string;
    seatmapBackgroundPathFill: string;
    seatmapBackgroundPathStroke: number;
    venueBackgroundPaths: Array<string>;

    specialSeatingRemovalConfirmPromptHeader: string;
    specialSeatingRemovalConfirmPromptMessage: string;

    productionPerformances: Array<Performance>;

    allPerformancesPassedModalTitle: string;
    allPerformancesPassedModalMessage: string;

    performanceNotOnSaleModalTitle: string;
    performanceNotOnSaleModalMessage: string;
    performanceSoldOutModalTitle: string,
    performanceSoldOutModalMessage: string,
    performanceNotOnSaleRedirectUrl: string;
    performanceNotOnSaleRedirectButtonText: string;

    ticketQuantityZeroModalTitle: string;
    ticketQuantityZeroModalMessage: string;
    ticketQuantityZeroRedirectUrl: string;
    ticketQuantityZeroRedirectButtonText: string;

    changeDateSelectionHeader: string;
    changeDateModalOKbutton: string;
    changeDateModalCancelbutton: string;

    seatmapLoadErrorTitle: string;
    seatmapLoadErrorMessage: string;
    seatmapLoadErrorBackButton: string;
    seatmapLoadErrorRedirectUrl: string;    

    performanceHasPassedText: string;

    clientSymbolSvgText: string;
    clientSymbolUrl: string;

    stopLoadingAnimationButtonText: string;

    venueName: string;
    venueLevels: Array<Level>;
    defaultSelectedLevelIds: Array<number>;

    allowExchangesAddon: boolean;
    exchangeTickets: Array<IExchangeTicket>;
    exchangeTokenExpiredErrorTitle: string;
    exchangeTokenExpiredErrorMessage: string;
    exchangeTokenFetchErrorTitle: string;
    exchangeTokenFetchErrorMessage: string;

    maxQuantityPriceTypeSelectedError: string;
    minimumSeatsSingleTicketRequiredMessage: string;
    maxQuantitySeatingOptionSelectedError: string;
    maxQuantityPriceTypeSelectedErrorTextReplace: string;
    priceTypeNotSelectedError: string;

    noMoreExchangeTicketsRemainingError: string;

    exchangeAdditionalTicketsRemainingMessage: string;
    exchangeTicketsRemainingMessage: string;
    exchangeCostRemainingMessage: string;
    availableAddOnsMessage: string;
    exchangeFreeLabel: string;

    isGeneralAdmissionVenue: boolean;
    isSelectSeatVenue: boolean;
    displayLevelSelectionOnLoad: boolean;

    displayCartTimer: boolean;
    cartTimerExpiringMinute: number;

    rightNavSvgText: string;
    rightNavSvgFile: string;
    rightNavUrl: string;

    performanceMessaging: string;
    pricingRulesMessaging: string;
    filterPageHeaderTitle: string;
    filterPageHeaderMessage: string;
    filterPageNoPricingMessage: string;

    packageQuantityStepOneButton: string;

    subscriptionFilterPageHeaderTitle: string;
    subscriptionFilterPageHeaderMessage: string;
    subscriptionQuantitySelectPageHeaderTitle: string;
    subscriptionQuantitySelectPageHeaderMessage: string;

    filterTicketStartingPrice: string;
    subscriptionFilterPackageTicketStartingPrice: string;

    minimumSeatsRequiredMessage: string;
    maximumSeatsRequiredMessage: string;

    singleClickSeatAddedMessage: string;
    singleClickSeatRemovedMessage: string;

    disableOneClick: boolean;
    disableOneClickRemove: boolean;
    enableZoneUpsells: boolean;
    disableSeatHover: boolean;
    disableMobileViewFromSeat: boolean;
    seatHoverMessageText: string;

    useZoneDescription: boolean;

    modalConfirmButtonText: string;
    modalCancelButtonText: string;
    emptyCartButtonText: string;

    noSeatsMatchFilterQuickCheckoutModalTitle: string;
    noSeatsMatchFilterQuickCheckoutModalMessage: string;

    onlyADASeatsMatchFilterGoToSeatmapModalTitle: string;
    onlyADASeatsMatchFilterGoToSeatmapModalMessage: string;
    showSeatingLegendOnPageLoad: boolean;
    showSeatingLegendAsModal: boolean;

    disableCartExpiredPopup: boolean;

    changeDateAndButtonText: string;
    reservingSeatMessage: string;

    viewFromSeatCaptionText: string;

    performanceListFilterTitle: string;
    performanceListMessage: string;
    promoCodeTitle: string;
    numberOfTicketsTitle: string;
    numberOfTicketsMessage: string;
    levelsHeading: string;
    levelsSectionMessage: string;
    levelAvailableCountMessage: string;
    showPricingOnLevelFilter: boolean;
    showLevelAvailabilityOnLevelFilter: boolean;

    enablePromoCodes: boolean;
    enableDateChange: boolean;
    showPromoCodeSectionOnPageLoad: boolean;
    selectAllPriceTypesOnSeatmap: boolean;
    selectOnlyDefaultPriceTypesOnSeatmap: boolean;
    useOnlyPromoPriceType: boolean;
    promoCodeApplicationSuccessMessage: string;
    promoCodeSuccessMessage: string;
    promoCodeApplicationErrorMessage: string;
    promoCodeConfirmApplyTitle: string;
    promoCodeConfirmApplyMessage: string;
    promoCodeConfirmApplyConfirm: string;
    promoCodeConfirmApplyCancel: string;
    cartTitle: string;
    refreshSeatmapTimestamp: Date;
    /**
     * On the filters page, the main message for the modal that pops up to notify the user that they will have to choose their own seats without suggested seats.
     */
    noSeatsMatchFilterGoToSeatmapModalMessage: string;
    /**
     * On the filters page, the title for the modal that pops up to notify the user that they will have to choose their own seats without suggested seats.
     */
    noSeatsMatchFilterGoToSeatmapModalTitle: string;
    /**
     * On the filters page, the text for the cancel button on the modal that pops up to notify the user that they will have to choose their own seats without suggested seats.
     */
    noSeatsMatchFilterGoToSeatmapModalCancel: string;
    /**
     * On the filters page, the text for the proceed button on the modal that pops up to notify the user that they will have to choose their own seats without suggested seats.
     */
    noSeatsMatchFilterGoToSeatmapModalProceed: string;

    onlyGALevelsSelectedGoToSeatmapModalMessage: string;
    onlyGALevelsSelectedGoToSeatmapModalTitle: string;
    onlyGALevelsSelectedGoToSeatmapModalCancel: string;
    onlyGALevelsSelectedGoToSeatmapModalProceed: string;

    /**
     * Determine if the price range component starts off expanded when the user enters Seatmap.
     */
    expandFiltersOnSeatmapLoad: boolean;

    expandLevelFilterOnSeatmapLoad: boolean;

    /**
     * Title for the price range filter on the seatmap.
     */
    seatmapPriceRangeFilterHeader: string;
    /**
     * Instructinos for the price range filter on the seatmap.
     */
    seatmapPriceRangeFilterInstructions: string;

    /**
     * Text to label the Filters button on the seatmap.
     */
    seatmapTicketTypeFilterHeader: string;
    /**
     * Instructions for the ticket type filter on the seatmap.
     */
    seatmapTicketTypeFilterInstructions: string;
    /**
     * Text to label the Filters button on the seatmap.
     */

    showLevelFilter: boolean;
    seatmapTicketTypeAllTicketsLabel: string;
    seatmapFiltersText: string;
    seatmapSelectedSeatText: string;
    seatmapSelectedZoneText: string;

    defaultAllLevelsSelected: boolean;

    flexInstructions: string;
    flexTotalLabel: string;
    flexPackageName: string;

    packagePerformances: Array<Performance>;
    packageTotalPriceInCart: number;
    packageSeatAmountInCart: number;
    packageFeeAmountInCart: number;
    currentItemIndex: number;
    packageTotalCount: number;
    packageProgressMessage: string;
    seatQuantityText: string;
    packagePriceText: string;
    showPackageProgressBar: boolean;
    showPackagePerformancesOnLoad: boolean;

    clientLeftNavLinkUrl: string;
    clientRightNavLinkUrl: string;

    ///**
    //* Upsells
    //*/
    upsellMessageText: string;
    upsellButtonText: string;
    zoneUpsellNumberOfTimesToUpsell: number;
    zoneUpsellEnableForMobile: boolean;

    showAttestationOnlyOnce: boolean;

    allowOrphanedSeats: boolean;
    disableSmartSeatPageTitle: boolean;
    combinePriceTypeSpecialSeatingFilters: boolean;
    showPriceTypesSeatMapFilter: boolean;
    showSpecialSeatingSeatMapFilter: boolean;
    hidePricesInCart: boolean;
    hidePricesOnSeatHover: boolean;
    subpackageHideMessage: string;

    // Seat Fee Updates
    displaySeatFee?: boolean;
    seatFeePreviewTitle?: string;
    seatFeePreviewMessage?: string;
    seatFeePreviewTicketTypeFormat?: string;
    priceTypeTextHeadingFormat?: string;
    seatFeePreviewCancelText?: string;
    seatFeePreviewSeatmapText?: string;
    seatFeePreviewConfirmText?: string;
    ticketCostText?: string;
    feeAmountText?: string;
    startingPriceFeeHeading?: string;
    priceFeeHeading?: string;
    venueLevelRangeFeesNote?: string;

    partialPackageTotalsLabel: string;

    seatZones?: Array<SeatZone>;

    // Promo code on packages
    disablePromoWhenCartNotEmpty: boolean,
    showDisabledPromoMessage: boolean,
    disabledPromoMessage: string

    // Cart expired
    cartExpiredTitle: string;
    cartExpiredMessage: string;
    cartExpiredConfirmText: string;

    showPerformanceTitleInDateModal: boolean;
    isDiagnosticModeActive: boolean;

    facilityConfigurationId: number;
    facilityConfigurationName: string;
    venueBuilderRootUrl: string;

    // Super Package conflicts.
    conflictSuperpackageIds?: number[];
    isSuperpackageConflictResolutionRequired?: boolean;
    superpackageConflictConfirmRedirectUrlQueryString?: string;
    superpackageConflictRejectRedirectUrlQueryString?: string;

    superpackageConflictModalTitle?: string;
    superpackageConflictModalMessage?: string;
    superpackageConflictModalConfirmButtonText?: string;
    superpackageConflictModalRejectButtonText?: string;

    // Flex package conflict properties
    isFlexpackageConflictResolutionRequired?: boolean;
    flexpackageConflictConfirmRedirectUrlQueryString?: string;
    flexpackageConflictRejectRedirectUrlQueryString?: string;

    flexPackageConflictModalTitle?: string;
    flexPackageConflictModalMessage?: string;
    flexPackageConflictModalConfirmButtonText?: string;
    flexPackageConflictModalRejectButtonText?: string;

    isSYOS: boolean
}

let defaultAppSettingModel: IAppSettingsModel = {
    currentCulture: null,
    disableSmartSeatPageTitle: false,
    altText: {
        clientSvgLogo: '',
        rightNavSymbolAltText: '',
        cartIcon: '',
        expandSectionArrow: '',
        filtersLevelSelection: '',
        legendIcon: '',
        levelMapSvg: '',
        seatMap: '',
        seatViewMagnifyingGlassSvg: '',
        selectedSeatSvg: ''
    },
    clientLeftNavLinkUrl: '',
    clientRightNavLinkUrl: '',
    googleAnalyticsId: '',
    itemId: 0,
    itemType: ItemType.Performance,
    language: null,
    isVariablePriceType: false,
    performanceDate: null,
    performanceDateString: '',
    performanceTimeZone: '',
    itemName: '',
    itemNameRichText: '',
    displayCartTimer: true,
    cartTimerMessage: '',
    cartTimerWithCountMessage: '',
    cartCount: 0,
    performanceTimeString: '',
    itemOnSale: true,
    itemAvailable: true,
    promoCodeOnly: false,
    priceTypeSettings: [],
    maxTicketQuantity: 8,
    minTicketQuantity: 0,
    groupTicketMessaging: '',
    reserveErrorMessage: '',
    seatFetchErrorMessage: '',
    cartUrl: '',
    myAccountUrl: '',
    enablePromoCodes: true,
    enableDateChange: true,
    currentPromoCode: null,
    svgDefaultFillColor: '',
    svgHoverFillColor: '',
    svgStrokeColor: '',
    svgStrokeWidth: 0,
    svgSelectedFillColor: '',
    showLevelFilter: true,
    seatHoverMessageText: '',
    renderAisleSeatToggle: 'false',
    renderAdditionalSeatingOptions: 'true',
    renderAdditionalSeatingOptionsBelowTicketQuantity: false,
    openAdditionalSeatingOptionsOnLoad: false,
    renderAdditionalNotes: true,
    showPerformanceListOnLoad: 'false',
    additionalSeatingOptions: [],
    additionalNotesPlaceholder: '',
    additionalNotesTitle: '',
    mapMaxZoom: 5,
    showZoomControlRight: false,
    mapScale: 1,
    mapScaleSensitivity: .25,
    seatmapSvgViewBox: '0 0 500 600',
    seatmapBackgroundImage: '',
    seatmapBackgroundPathFill: '',
    seatmapBackgroundPathStroke: 0,
    venueBackgroundPaths: [],
    currencyFormat: '',
    currencyFormatCountries: [],
    loadingText: '',
    loadingSvgText: '',
    cartConflictErrorHeading: '',
    cartConflictErrorMessageText: '',
    seatIsInCartMessage: '',
    groupSeatsInCartMessage: '',
    groupSeatCartItemText: '',
    seatsRemainingText: '',
    seatHoverMissingPriceTypesMessage: '',
    cartSubtotalLabel: '',
    cartEmptyNotice: '',
    cartAddMoreButton: '',
    cartCheckoutButton: '',
    cartMessage: '',
    missingAttestationErrorMessageHeader: '',
    missingAttestationErrorMessageText: '',
    missingAttestationErrorMessageButton: '',
    attestationSeatCancelButton: '',
    attestationSeatConfirmationButton: '',
    leaveOrphanedSeatsMessage: '',
    filterFoundToast: '',
    filterSearchingToast: '',
    filterToastThreshold: '',
    filterErrorToast: '',
    reservingSeatMessage: '',
    changeDateSelectionHeader: '',
    changeDateModalOKbutton: '',
    changeDateModalCancelbutton: '',
    cartTimerExpiringMinute: 0,

    filterProceedButton: '',
    filterQuickCheckoutButton: '',
    addToCartButtonText: '',
    updateCartButtonText: '',
    viewCartButtonText: '',
    goToCartButtonText: '',

    priceTypeCriteriaNotReachedShowAsModal: false,
    forcePatronsReserveInMultiples: 0,
    forcePatronsReserveWholeRow: false,
    forceMultipleCartMessageText: "",
    forceMultipleFilterMessageText: "",
    minimumTicketsPerSeatGroup: 0,

    legendText: '',
    legendModalText: '',
    changeLevelsButtonText: '',
    levelAvailableCountMessage: '',

    viewFromSeatCaptionText: '',

    filterDateAndTimeTitle: '',
    filterPriceRangeTitle: '',
    filterPriceRangeMessage: '',
    filterPriceRangeMin: '',
    filterPriceRangeMax: '',
    clearButton: '',

    seatmapSelectedZoneText: '',
    missingSelectedSeatingOptionNotice: '',

    aisleAccessSeatsLabel: '',
    canBeSeparatedLabel: '',

    filterAdditionalSeatingOptionsTitle: '',
    filterAdditionalSeatingOptionsMessage: '',
    productionPerformances: [],
    promoCodeApplicationErrorMessage: '',
    promoCodeApplicationSuccessMessage: '',
    promoCodeSuccessMessage: '',
    promoCodeConfirmApplyCancel: '',
    promoCodeConfirmApplyConfirm: '',
    promoCodeConfirmApplyMessage: '',
    promoCodeConfirmApplyTitle: '',
    allPerformancesPassedModalTitle: '',
    allPerformancesPassedModalMessage: '',
    performanceNotOnSaleModalTitle: '',
    performanceNotOnSaleModalMessage: '',
    performanceSoldOutModalTitle: '',
    performanceSoldOutModalMessage: '',
    performanceNotOnSaleRedirectUrl: '',
    performanceNotOnSaleRedirectButtonText: '',

    ticketQuantityZeroModalTitle: '',
    ticketQuantityZeroModalMessage: '',
    ticketQuantityZeroRedirectUrl: '',
    ticketQuantityZeroRedirectButtonText: '',

    performanceHasPassedText: '',
    clientSymbolSvgText: '',
    clientSymbolUrl: '',

    seatmapLoadErrorTitle: '',
    seatmapLoadErrorMessage: '',
    seatmapLoadErrorBackButton: '',
    seatmapLoadErrorRedirectUrl: '',    

    stopLoadingAnimationButtonText: '',

    venueName: '',
    venueLevels: [],
    defaultSelectedLevelIds: [],

    allowExchangesAddon: true,
    exchangeTickets: [],
    exchangeTokenExpiredErrorMessage: "",
    exchangeTokenExpiredErrorTitle: "",
    exchangeTokenFetchErrorTitle: "",
    exchangeTokenFetchErrorMessage: "",
    availableAddOnsMessage: "",
    maxQuantityPriceTypeSelectedError: "",
    minimumSeatsSingleTicketRequiredMessage: "",
    maxQuantityPriceTypeSelectedErrorTextReplace: "",
    maxQuantitySeatingOptionSelectedError: "",
    priceTypeNotSelectedError: "",
    noMoreExchangeTicketsRemainingError: "",
    onlyADASeatsMatchFilterGoToSeatmapModalMessage: "",
    onlyADASeatsMatchFilterGoToSeatmapModalTitle: "",
    exchangeAdditionalTicketsRemainingMessage: "",
    exchangeTicketsRemainingMessage: "",
    exchangeCostRemainingMessage: "",

    exchangeFreeLabel: "",
    isGeneralAdmissionVenue: false,
    isSelectSeatVenue: false,
    displayLevelSelectionOnLoad: true,
    brandId: '',
    singleClickSeatAddedMessage: '',
    singleClickSeatRemovedMessage: '',

    rightNavSvgText: '',
    rightNavSvgFile: '',
    rightNavUrl: '',
    performanceMessaging: '',
    pricingRulesMessaging: '',
    disableOneClick: false,
    disableOneClickRemove: false,
    enableZoneUpsells: false,
    disableSeatHover: true,
    disableMobileViewFromSeat: false,
    showSeatingLegendOnPageLoad: false,
    showSeatingLegendAsModal: true,
    disableCartExpiredPopup: false,
    filterPageHeaderMessage: '',
    filterPageHeaderTitle: '',
    filterPageNoPricingMessage: '',
    packageQuantityStepOneButton: '',

    subscriptionFilterPageHeaderTitle: '',
    subscriptionFilterPageHeaderMessage: '',
    subscriptionQuantitySelectPageHeaderTitle: '',
    subscriptionQuantitySelectPageHeaderMessage: '',
    subscriptionFilterPackageTicketStartingPrice: '',
    filterTicketStartingPrice: '',
    minimumSeatsRequiredMessage: '',
    maximumSeatsRequiredMessage: '',

    specialSeatingRemovalConfirmPromptHeader: '',
    specialSeatingRemovalConfirmPromptMessage: '',
    modalConfirmButtonText: '',
    modalCancelButtonText: '',
    emptyCartButtonText: '',

    noSeatsMatchFilterQuickCheckoutModalTitle: '',
    noSeatsMatchFilterQuickCheckoutModalMessage: '',
    changeDateAndButtonText: '',

    performanceListFilterTitle: '',
    performanceListMessage: '',
    promoCodeTitle: '',
    numberOfTicketsTitle: '',
    numberOfTicketsMessage: '',
    levelsHeading: '',
    levelsSectionMessage: '',

    showPromoCodeSectionOnPageLoad: true,
    cartTitle: '',
    useZoneDescription: false,
    noSeatsMatchFilterGoToSeatmapModalMessage: '',
    noSeatsMatchFilterGoToSeatmapModalTitle: '',
    noSeatsMatchFilterGoToSeatmapModalCancel: '',
    noSeatsMatchFilterGoToSeatmapModalProceed: '',

    onlyGALevelsSelectedGoToSeatmapModalMessage: '',
    onlyGALevelsSelectedGoToSeatmapModalTitle: '',
    onlyGALevelsSelectedGoToSeatmapModalCancel: '',
    onlyGALevelsSelectedGoToSeatmapModalProceed: '',

    expandFiltersOnSeatmapLoad: true,
    expandLevelFilterOnSeatmapLoad: true,
    selectOnlyDefaultPriceTypesOnSeatmap: false,
    venueSvgHighlightShading: '.5',
    seatmapPriceRangeFilterHeader: '',
    seatmapPriceRangeFilterInstructions: '',
    seatmapTicketTypeFilterHeader: '',
    seatmapTicketTypeFilterInstructions: '',
    seatmapTicketTypeAllTicketsLabel: '',
    seatmapFiltersText: '',
    seatmapSelectedSeatText: '',
    showLevelAvailabilityOnLevelFilter: false,
    showPricingOnLevelFilter: false,
    refreshSeatmapTimestamp: null,
    currentItemIndex: 0,
    packageTotalCount: 0,
    flexInstructions: '',
    flexTotalLabel: '',
    flexPackageName: '',
    packagePerformances: [],
    packageTotalPriceInCart: 0,
    packageFeeAmountInCart: 0,
    packageSeatAmountInCart: 0,
    packageProgressMessage: '',
    seatQuantityText: '',
    packagePriceText: '',
    useOnlyPromoPriceType: false,
    selectAllPriceTypesOnSeatmap: true,
    upsellMessageText: '',
    upsellButtonText: '',
    defaultAllLevelsSelected: true,
    gaZoneSelectTicketMessage: '',
    zoneUpsellNumberOfTimesToUpsell: 0,
    zoneUpsellEnableForMobile: true,

    showPackagePerformancesOnLoad: true,
    showPackageProgressBar: true,

    showAttestationOnlyOnce: true,
    allowOrphanedSeats: true,
    combinePriceTypeSpecialSeatingFilters: false,
    showPriceTypesSeatMapFilter: true,
    showSpecialSeatingSeatMapFilter: true,
    requesting: false,
    hidePricesInCart: false,
    hidePricesOnSeatHover: false,
    subpackageHideMessage: "",

    seatFeePreviewTitle: '',
    seatFeePreviewMessage: '',
    seatFeePreviewTicketTypeFormat: '',
    priceTypeTextHeadingFormat: '',
    ticketCostText: '',
    feeAmountText: '',
    startingPriceFeeHeading: '',
    priceFeeHeading: '',
    venueLevelRangeFeesNote: '',

    seatZones: [],
    // Promo code on packages
    disablePromoWhenCartNotEmpty: true,
    showDisabledPromoMessage: true,
    disabledPromoMessage: '',
    cartExpiredTitle: 'Cart Expired',
    cartExpiredMessage: 'Start selecting tickets again.',
    cartExpiredConfirmText: 'OK',
        
    showPerformanceTitleInDateModal: false,
    isDiagnosticModeActive: false,

    facilityConfigurationId: 0,
    facilityConfigurationName: '',
    venueBuilderRootUrl: '',
    partialPackageTotalsLabel: '',

    isSYOS: false
};

export { defaultAppSettingModel };

export function appSettingsModel(state: IAppSettingsModel = defaultAppSettingModel, action: IFetchStateAction) {
    switch (action.type) {
        //case ActionTypes.LOAD_LOCAL_STORAGE:
        //    return { ...state, ...action.value.appSettingsModel }
        case ActionTypes.REQUEST_SETTINGS:
            return { ...state, requesting: true };
        case ActionTypes.RECEIVE_SETTINGS:
            let newSettings = action.value as IAppSettingsModel;
            if (state.itemType == ItemType.Exchange) {
                newSettings.maxTicketQuantity = state.maxTicketQuantity;
            }
            if (newSettings.mapScaleSensitivity > 1) {
                newSettings.mapScaleSensitivity = newSettings.mapScaleSensitivity * .01;
            }

            //IE11 need to disable seat hover
            if (!!navigator.userAgent.match(/Trident\/7\./)) {
                newSettings.disableSeatHover = true;
            }

            return { ...state, ...newSettings, requesting: false };
        case ActionTypes.SET_MAX_TICKET_QUANTITY:
            return { ...state, maxTicketQuantity: action.value };
        case ActionTypes.MAX_TICKETS_SELECTED:
            return { ...state, maxQuantityPriceTypeSelectedErrorTextReplace: action.value.priceTypeText };
        default:
            return state
    }
}